import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Twitter, Linkedin, Github } from "lucide-react";

interface FooterLink {
  label?: string;
  href: string;
}

interface FooterNavSection {
  title?: string;
  links: FooterLink[];
}

interface SocialLink {
  platform: string;
  url: string;
}

interface FooterProps {
  navigation: FooterNavSection[];
  social?: SocialLink[];
}

const socialIcons: Record<string, React.ElementType> = {
  twitter: Twitter,
  linkedin: Linkedin,
  github: Github,
};

/**
 * Footer section title keys, ordered to match the navigation array from seed-data.
 * These map to common.json: footer.sections.product, footer.sections.company, etc.
 */
const sectionKeys = ["product", "company", "resources", "legal"] as const;

/**
 * Map of href to translation key in common.json footer.links.*
 */
const hrefToLinkKey: Record<string, string> = {
  "/features": "features",
  "/pricing": "pricing",
  "/security": "security",
  "/integrations": "integrations",
  "/changelog": "changelog",
  "/about": "about",
  "/blog": "blog",
  "/careers": "careers",
  "/contact": "contact",
  "/docs": "documentation",
  "/help": "helpCenter",
  "/api": "apiReference",
  "/community": "community",
  "/privacy": "privacyPolicy",
  "/terms": "termsOfService",
  "/cookies": "cookiePolicy",
  "/gdpr": "gdpr",
};

/**
 * Footer
 *
 * Site footer with:
 * - Company info and description
 * - Multi-column navigation
 * - Social links
 * - Copyright
 * - All text from translations
 */
export function Footer({ navigation, social }: FooterProps) {
  const { t } = useTranslation("common");

  const companyName = t("footer.company.name");
  const companyDescription = t("footer.company.description");
  const companyCopyright = t("footer.company.copyright");

  return (
    <footer className="relative border-t border-border bg-muted">
      <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-6 lg:gap-8">
          {/* Company info */}
          <div className="lg:col-span-2">
            <div className="mb-4 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <span className="text-sm font-bold text-primary-foreground">A</span>
              </div>
              <span className="text-lg font-semibold text-foreground">{companyName}</span>
            </div>
            <p className="mb-6 max-w-xs text-sm leading-relaxed text-muted-foreground">
              {companyDescription}
            </p>

            {/* Social links */}
            {social && social.length > 0 && (
              <div className="flex items-center gap-3">
                {social.map((link) => {
                  const Icon = socialIcons[link.platform] || Github;
                  return (
                    <a
                      key={link.platform}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "flex h-9 w-9 items-center justify-center rounded-lg",
                        "bg-background hover:bg-background/80",
                        "text-muted-foreground hover:text-foreground",
                        "border border-border hover:border-border",
                        "transition-all duration-200",
                      )}
                      aria-label={`Follow us on ${link.platform}`}
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Navigation columns */}
          {navigation.map((section, sectionIndex) => {
            const sectionKey = sectionKeys[sectionIndex];
            const sectionTitle = sectionKey
              ? t(`footer.sections.${sectionKey}`, { defaultValue: section.title })
              : section.title;

            return (
              <div key={sectionIndex}>
                <h4 className="mb-4 text-sm font-semibold text-foreground">{sectionTitle}</h4>
                <ul className="space-y-3">
                  {section.links.map((link) => {
                    const linkKey = hrefToLinkKey[link.href];
                    const linkLabel = linkKey
                      ? t(`footer.links.${linkKey}`, { defaultValue: link.label })
                      : link.label;

                    return (
                      <li key={link.href}>
                        <a
                          href={link.href}
                          className={cn(
                            "text-sm text-muted-foreground",
                            "hover:text-foreground",
                            "transition-colors duration-200",
                          )}
                        >
                          {linkLabel}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Bottom bar */}
        <div
          className={cn(
            "mt-16 pt-8",
            "border-t border-border",
            "flex flex-col items-center justify-between gap-4 sm:flex-row",
          )}
        >
          <p className="text-sm text-muted-foreground">{companyCopyright}</p>
          <div className="flex items-center gap-6">
            <a
              href="/privacy"
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t("footer.links.privacyPolicy", { defaultValue: "Privacy" })}
            </a>
            <a
              href="/terms"
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t("footer.links.termsOfService", { defaultValue: "Terms" })}
            </a>
            <a
              href="/cookies"
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t("footer.links.cookiePolicy", { defaultValue: "Cookies" })}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
